import { useEffect, useState } from 'react';
import { useSaveViewStore } from '../saveViewFeature';
import { Decoder } from '../filters/Decoder';
import { SearchParam, useSavedViewParam } from '../filters/SearchParam';
import { useTableName } from './TableDataProvider';
import { TableSessionState } from './TableSessionState';

/**
 * When the URL has no `f` param, restore state in order:
 * 1. Last unsaved session state for this table (same tab)
 * 2. User's default saved view
 *
 * Shareable links (`?f=...`) always win and skip both.
 */
export const useLoadSavedView = () => {
  const [isLoading, setIsLoading] = useState(true);
  const tableName = useTableName();
  const { updateSavedViewParam } = useSavedViewParam();
  const defaultViewData = useSaveViewStore((state): string | undefined => {
    const defaultView = state.defaultViews[tableName];
    const savedView = state.savedViews[tableName];

    if (!defaultView || !savedView) {
      return undefined;
    }

    return savedView[defaultView]?.data;
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: hydrate once on mount from URL / session / default view
  useEffect(() => {
    const load = async () => {
      const f = SearchParam.get();

      if (!f) {
        const sessionState = TableSessionState.get(tableName);
        const restoredFromSession = await tryRestoreSessionState(tableName, sessionState, updateSavedViewParam);

        if (!restoredFromSession && defaultViewData) {
          await updateSavedViewParam(defaultViewData);
        }
      }

      setIsLoading(false);
    };

    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { isLoading };
};

const tryRestoreSessionState = async (
  tableName: string,
  sessionState: string | null,
  updateSavedViewParam: (value: string | null) => Promise<void>,
): Promise<boolean> => {
  if (!sessionState) {
    return false;
  }

  try {
    await Decoder.decode(sessionState);
    await updateSavedViewParam(sessionState);
    return true;
  } catch {
    TableSessionState.set(tableName, null);
    return false;
  }
};
